import React, { useState } from "react";
import { Maximize2, Radio } from "lucide-react";

// Deterministic-ish pseudo-random based on string -> [0,1]
const seed = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h % 1000) / 1000;
};

/**
 * Layers fake bounding boxes over a placeholder camera image.
 * Boxes are stable per camera id + index so they don't jitter wildly between renders,
 * but use CSS animation to drift slightly.
 */
export const CameraTile = ({ camera, hasAlert, onExpand }) => {
  const [imgError, setImgError] = useState(false);

  const boxCount = 3 + Math.floor(seed(camera.id + "n") * 4); // 3..6
  const boxes = Array.from({ length: boxCount }, (_, i) => {
    const r1 = seed(camera.id + ":b" + i);
    const r2 = seed(camera.id + ":x" + i);
    const r3 = seed(camera.id + ":y" + i);
    const r4 = seed(camera.id + ":w" + i);
    const r5 = seed(camera.id + ":h" + i);
    return {
      x: 5 + r2 * 80,
      y: 25 + r3 * 60,
      w: 6 + r4 * 12,
      h: 12 + r5 * 18,
      critical: hasAlert && r1 > 0.7,
    };
  });

  return (
    <div
      className="relative aspect-video bg-black border border-border overflow-hidden group"
      data-testid={`camera-tile-${camera.id}`}
    >
      {!imgError ? (
        <img
          src={camera.image_url}
          alt={camera.name}
          onError={() => setImgError(true)}
          className="absolute inset-0 w-full h-full object-cover opacity-90"
          style={{ filter: "contrast(1.05) saturate(0.85)" }}
        />
      ) : (
        <div className="absolute inset-0 grid-bg" />
      )}

      {/* Scanline overlay */}
      <div
        className="absolute inset-0 pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.04) 1px, transparent 1px, transparent 3px)",
        }}
      />

      {/* Bounding boxes */}
      {boxes.map((b, i) => (
        <div
          key={i}
          className="absolute bbox-drift"
          style={{
            left: `${b.x}%`,
            top: `${b.y}%`,
            width: `${b.w}%`,
            height: `${b.h}%`,
            border: `1.5px solid ${
              b.critical ? "hsl(var(--destructive))" : "hsl(var(--primary))"
            }`,
            boxShadow: b.critical ? "0 0 12px hsl(var(--destructive)/0.6)" : "none",
            animationDelay: `${i * 0.4}s`,
          }}
        >
          <div
            className="absolute -top-3.5 left-0 px-1 font-mono text-[9px] uppercase tracking-widest"
            style={{
              color: b.critical ? "hsl(var(--destructive))" : "hsl(var(--primary))",
              background: "rgba(0,0,0,0.7)",
            }}
          >
            id:{(i + 101).toString(16)}
          </div>
        </div>
      ))}

      {/* Camera ID overlay (top-right) */}
      <div className="absolute top-1 right-1 bg-black/60 px-1.5 py-0.5 font-mono text-[10px] text-[hsl(var(--primary))] flex items-center gap-1">
        <span className={`w-1.5 h-1.5 rounded-full ${camera.status === "online" ? "bg-[hsl(var(--success))]" : "bg-[hsl(var(--warning))]"}`} />
        {camera.id}
      </div>

      {/* Bottom strip */}
      <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-gradient-to-t from-black/85 to-transparent flex items-end justify-between">
        <div className="min-w-0">
          <div className="font-display font-bold text-[11px] uppercase tracking-tight text-white truncate">
            {camera.name}
          </div>
          <div className="font-mono text-[9px] text-white/60 uppercase tracking-widest">
            REC · {new Date().toISOString().slice(11, 19)}
          </div>
        </div>
        {hasAlert && (
          <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest sev-critical">
            <Radio className="w-3 h-3" /> ALERT
          </div>
        )}
      </div>

      {/* Expand on hover */}
      <button
        onClick={() => onExpand?.(camera)}
        data-testid={`camera-expand-${camera.id}`}
        className="absolute top-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity btn-tac-primary !py-0.5 !px-1.5 !text-[10px]"
      >
        <Maximize2 className="w-3 h-3" /> Focus
      </button>
    </div>
  );
};

export default CameraTile;
